import { useEffect, useMemo, useState } from 'react'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import Location from '../../hooks/Location'
import useHierarchyData from '../../hooks/useHierarchyData'
import defaultDates from '../../utils/defaultDates'
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis'
import getChainType from '../../helpers/dataHelpers/matrixDataHelpers/getChainType'
import PageHeader from '../../components/layout/PageHeader'
import Table from '../../components/common/table/Table'
import spinner from '../../images/spinner-ripple.svg'
import notDeleted from '../../helpers/notDeleted'

const chainTypeCache = new Map()

/**
 * Convert a Firestore Timestamp (in any serialized form) to a JS Date.
 */
const toDate = (ts) => {
    if (!ts) return null
    if (ts.toDate) return ts.toDate()
    if (ts._seconds != null) return new Date(ts._seconds * 1000)
    if (ts.seconds != null) return new Date(ts.seconds * 1000)
    if (typeof ts === 'number') return new Date(ts)
    return null
}

const formatDate = (ts) => {
    const date = toDate(ts)
    if (!date || isNaN(date.getTime())) return '—'
    return date.toLocaleDateString('nl-NL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })
}

const ResponseDetails = () => {
    const fieldId = Location()[3]
    const [chainType, setChainType] = useState(null)
    const [chainTypeLoading, setChainTypeLoading] = useState(true)
    const [researchTitles, setResearchTitles] = useState({})
    const [siblingFields, setSiblingFields] = useState(null)

    const { defaultStartDate, defaultEndDate } = useMemo(() => defaultDates(), [])
    const startMs = useMemo(() => toMillis(defaultStartDate) ?? 0, [defaultStartDate])
    const endMs = useMemo(() => toMillis(defaultEndDate) ?? 8640000000000000, [defaultEndDate])

    // Detect chain type (Question vs MSIEffect)
    useEffect(() => {
        if (!fieldId) return
        setChainTypeLoading(true)
        getChainType(fieldId, db, chainTypeCache)
            .then(t => { setChainType(t); setChainTypeLoading(false) })
            .catch(() => { setChainType('Question'); setChainTypeLoading(false) })
    }, [fieldId])

    // Find all sibling fields with the same MSIId (to debug benchmark mismatch)
    useEffect(() => {
        if (!fieldId) return

        db.collection('QuestionnaireFields')
            .where('ID', '==', fieldId)
            .limit(1)
            .get()
            .then(snap => {
                if (snap.empty) return
                const msiId = snap.docs[0].data().MSIId
                if (!msiId) { setSiblingFields([]); return }

                return db.collection('QuestionnaireFields')
                    .where('MSIId', '==', msiId)
                    .get()
                    .then(allSnap => {
                        setSiblingFields(allSnap.docs.filter(d => notDeleted(d.data())).map(d => {
                            const data = d.data()
                            return { id: data.ID, companyId: data.CompagnyID, msiId: data.MSIId }
                        }))
                    })
            })
    }, [fieldId])

    const { records, loading: recordsLoading, error } = useHierarchyData({
        syncType: chainType,
        id: fieldId,
        startDate: startMs,
        endDate: endMs,
    })

    // Batch-lookup research titles for all unique ResearchIDs
    useEffect(() => {
        if (!records || records.length === 0) return

        const ids = [...new Set(records.map(r => r.ResearchID).filter(Boolean))]
        const missing = ids.filter(id => !researchTitles[id])
        if (missing.length === 0) return

        const chunks = []
        for (let i = 0; i < missing.length; i += 10) chunks.push(missing.slice(i, i + 10))

        Promise.all(
            chunks.map(chunk =>
                db.collection('Research')
                    .where('ID', 'in', chunk)
                    .get()
            )
        ).then(snaps => {
            const titles = { ...researchTitles }
            snaps.forEach(snap => {
                snap.docs.forEach(d => {
                    const data = d.data()
                    if (data.ID) titles[data.ID] = data.Title || data.ID
                })
            })
            setResearchTitles(titles)
        })
    }, [records])

    const loading = chainTypeLoading || recordsLoading

    // Sort by Timestamp descending
    const sortedRecords = useMemo(() => {
        if (!records || records.length === 0) return []
        return [...records].sort((a, b) => {
            const tA = toDate(a.Timestamp)?.getTime() ?? 0
            const tB = toDate(b.Timestamp)?.getTime() ?? 0
            return tB - tA
        })
    }, [records])

    const columns = [
        { key: 'Timestamp', label: 'Datum' },
        { key: 'Input', label: 'Antwoord' },
        { key: 'RowTitle', label: 'Vraag' },
        { key: 'MomentPosition', label: 'Meetmoment' },
        { key: 'ResearchID', label: 'Onderzoek' },
        { key: '_projectName', label: 'Project' },
    ]

    if (loading) return <img src={spinner} alt='loading' />
    if (error) return <p>Fout bij laden van gegevens.</p>

    const currentFieldSiblings = siblingFields?.filter(f => f.id !== fieldId) || []

    return (
        <div>
            <PageHeader heading='Antwoorden details' />
            <p>{sortedRecords.length} records voor veld {fieldId}</p>

            {siblingFields && currentFieldSiblings.length > 0 && (
                <div className='bd-position-block' style={{ marginBottom: '16px', padding: '12px 16px' }}>
                    <p><strong>Let op:</strong> De benchmark ({siblingFields.length} velden met hetzelfde MSI effect) bevat ook deze velden:</p>
                    <ul>
                        {currentFieldSiblings.map(f => (
                            <li key={f.id}>
                                <span style={{ fontSize: '0.8rem', color: '#888' }}>{f.id}</span>
                                {' '}— organisatie: {f.companyId}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {siblingFields && currentFieldSiblings.length === 0 && (
                <p style={{ color: 'green', fontSize: '0.85rem' }}>Dit is het enige veld voor dit MSI effect.</p>
            )}

            <Table
                columns={columns}
                items={sortedRecords}
                renderRow={(item) => (
                    <>
                        <td>{formatDate(item.Timestamp)}</td>
                        <td>{item.Input}</td>
                        <td>{item.RowTitle || '—'}</td>
                        <td>{item.MomentPosition != null ? `Meetmoment ${item.MomentPosition}` : '—'}</td>
                        <td>{researchTitles[item.ResearchID] || item.ResearchID || '—'}</td>
                        <td>{item._projectName || client}</td>
                    </>
                )}
                searchAccessor={(item) => `${item.Input || ''} ${item.RowTitle || ''} ${researchTitles[item.ResearchID] || ''}`}
                emptyMessage='Geen antwoorden gevonden.'
                showSearch={true}
            />
        </div>
    )
}

export default ResponseDetails
